import React, { useEffect, useState } from "react";
import Button from "../Button";
import DataTable from "../DataTable";
import Modal from "./Modal";
import moment from "moment";
import ModalCertificate from "./ModalCertificate";
import ModalConfirmation from "./ModalConfirmation";
import { monthsAndResourceTypes } from "../../constants/translations";
import { convertAmountToFrontend } from "../../helpers/convert";

const BilateralDynamicModal = ({
  t,
  isOpen,
  toggle,
  selectedAgreement,
  cancelMode,
  cancelOnclick,
  loading,
  organizationId,
}) => {
  const [cancelDesc, setCancelDesc] = useState(
    cancelMode ? "" : selectedAgreement.note
  );
  const [inspectedCertificate, setInspectedCertificate] = useState({});
  const [showModalConfirmation, setShowModalConfirmation] = useState(false);

  let certificates = [];
  if (Object.keys(selectedAgreement).length !== 0) {
    certificates = selectedAgreement.bilateralAgreementDetails.reduce(
      (acc, value) => {
        acc.push({
          ...value.yekgCertificate,
          bilateralAmount: value.amount,
          stationName: value.yekgCertificate.productionData.station.stationName,
          productionMonth: moment(
            value.yekgCertificate.productionData.term
          ).format("MM"),
          productionYear: moment(
            value.yekgCertificate.productionData.term
          ).format("YYYY"),
          productionPeriod: value.yekgCertificate.productionData.term,
          bilateralStartNo: value.documentStartNo,
          bilateralEndNo: value.documentEndNo,
        });
        return acc;
      },
      []
    );
  }

  const translations = monthsAndResourceTypes(t);

  useEffect(() => {
    setCancelDesc(selectedAgreement.note ? selectedAgreement.note : "");
  }, [selectedAgreement]);

  const activeCertificatesColumns = [
    {
      name: t("pages.default.certificates.table.stationName"),
      selector: "stationName",
      sortable: true,
    },
    {
      name: t("pages.default.certificates.table.resourceType"),
      selector: "resourceType",
      sortable: true,
      cell: (row) => translations[row.resourceType],
    },
    {
      name: t("pages.default.certificates.table.productionPeriod"),
      selector: "month",
      sortable: true,
      cell: (row) => {
        return `${translations[row.month]}/${row.year}`;
      },
    },
    {
      name: t("pages.default.certificates.table.documentCount"),
      selector: "bilateralAmount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.bilateralAmount);
      },
    },
    {
      name: t("pages.default.certificates.table.process"),
      selector: "process",
      sortable: false,
      cell: (row) => {
        return (
          <div style={{ display: "flex" }}>
            <Button
              suffixIcon="search"
              type="secondary"
              onClick={() => setInspectedCertificate(row)}
            />
          </div>
        );
      },
    },
  ];

  const handleCancelOnClick = () => {
    cancelOnclick({
      status: "CANCELED",
      note: cancelDesc,
    }).then(() => {
      setShowModalConfirmation(false);
    });
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle}>
        <div className="km__c--modal-bilateral-body">
          <div className="km__c--modal-bilateral-header km__u--text_title">
            <div>
              {t("pages.default.bilateralAgreements.modal.createDateTitle")}
            </div>
            <div>
              {selectedAgreement.createdDate
                ? moment(selectedAgreement.createdDate).format(
                    "DD-MM-YYYY HH:mm:ss"
                  )
                : "-"}
            </div>
          </div>
          <div className="km__c--modal-bilateral-header km__u--text_title">
            <div>
              {t(
                "pages.default.bilateralAgreements.modal.buyerOrganizationTitle"
              )}
            </div>
            <div>
              {organizationId === selectedAgreement.toOrganizationId
                ? selectedAgreement.fromOrganizationName
                : selectedAgreement.toOrganizationName}
            </div>
          </div>
          {cancelMode || selectedAgreement.status === "CANCELED" ? (
            <div className="km__c--modal-bilateral-header km__u--text_title">
              <div className="cancel-desc">
                {t("pages.default.bilateralAgreements.modal.cancelDesc")}
              </div>
              <textarea
                className="km__c--input"
                minLength={10}
                maxLength={255}
                value={cancelDesc}
                onChange={(e) => setCancelDesc(e.target.value)}
                style={{
                  minWidth: "40%",
                  maxWidth: "70%",
                  minHeight: 120,
                  maxHeight: 120,
                }}
                disabled={selectedAgreement.status === "CANCELED"}
              />
            </div>
          ) : (
            selectedAgreement.status !== "INITIAL" && (
              <div className="km__c--modal-bilateral-header km__u--text_title">
                <div>
                  {t(
                    "pages.default.bilateralAgreements.modal.approveDateTitle"
                  )}
                </div>
                <div>
                  {selectedAgreement.agreementDate
                    ? moment(selectedAgreement.agreementDate).format(
                        "DD-MM-YYYY HH:mm:ss"
                      )
                    : "-"}
                </div>
              </div>
            )
          )}
          <div className="km__c--modal-bilateral-table-header km__u--text_title">
            <div>
              {t(
                "pages.default.bilateralAgreements.modal.subjectToBilateralTitle"
              )}
            </div>
          </div>
          <DataTable
            columns={activeCertificatesColumns}
            data={certificates}
            rowKey={"id"}
            pagination
            paginationPerPage={4}
          />
          <div className="km__c--modal-bilateral-execute-container">
            <div className="km__c--modal-bilateral-amount-container">
              <div className="km__u--text_title">
                {t("pages.default.bilateralAgreements.totalAmountTitle")}
              </div>
              <div className="km__p--bilateral-agreements-total-amount">
                {convertAmountToFrontend(selectedAgreement.amount)}
              </div>
            </div>
            <div className="km__c--modal-bilateral-buttons-container">
              <Button
                type="secondary"
                label={t("ui.default.close")}
                onClick={toggle}
                disabled={loading}
              />
              {cancelMode && (
                <Button
                  type="primary"
                  label={t("ui.default.approve")}
                  onClick={() => setShowModalConfirmation(true)}
                  disabled={cancelDesc.trim().length < 10 || loading}
                />
              )}
            </div>
          </div>
        </div>
      </Modal>
      <ModalConfirmation
        isOpen={showModalConfirmation}
        toggle={() => setShowModalConfirmation(false)}
        header={t(
          "pages.default.bilateralAgreements.bilateralCancelConfirmation.header"
        )}
        body={t(
          "pages.default.bilateralAgreements.bilateralCancelConfirmation.body"
        )}
        approve={handleCancelOnClick}
        loading={loading}
      />
      {inspectedCertificate.id && (
        <ModalCertificate
          t={t}
          certificateId={inspectedCertificate.id}
          isOpen={inspectedCertificate.id && true}
          toggle={() => setInspectedCertificate({})}
          amount={inspectedCertificate.bilateralAmount}
          startNo={inspectedCertificate.bilateralStartNo}
          endNo={inspectedCertificate.bilateralEndNo}
          requestUrl={`bilateralAgreement/${selectedAgreement.id}/`}
          difCalculate={true}
        />
      )}
    </>
  );
};

export default BilateralDynamicModal;
